import React from 'react';
import { createBrowserRouter, useParams, Navigate } from 'react-router-dom';

// Import all top-level components
import App from './App.jsx';
import Login from './pages/Login.jsx';
import AppLayout from './layouts/AppLayout.jsx';
import BusinessDashboard from './pages/BusinessDashboard.jsx';
import OrdersPage from './pages/OrdersPage.jsx';
import ServicesPage from './pages/ServicesPage.jsx';
import InventoryPage from './pages/InventoryPage.jsx';

// V2 Imports
import AgendaPage from './pages/AgendaPage.jsx';
import KanbanPage from './pages/KanbanPage.jsx';
import CustomersPage from './pages/CustomersPage.jsx';
import CommissionsPage from './pages/CommissionsPage.jsx';
import CampaignsPage from './pages/CampaignsPage.jsx';
import LoyaltyPage from './pages/LoyaltyPage.jsx';
import AcademyPage from './pages/AcademyPage.jsx';
import FinancesPage from './pages/FinancesPage.jsx';
import StaffPage from './pages/StaffPage.jsx';
import ConfigPage from './pages/ConfigPage.jsx';
import MinisitioPage from './pages/MinisitioPage.jsx';
import AccountPage from './pages/AccountPage.jsx';
import MenuMediaPage from './pages/MenuMediaPage.jsx';
import ProspectingPage from './pages/ProspectingPage.jsx';
import CatalogPage from './pages/CatalogPage.jsx';
import QuotesPage from './pages/QuotesPage.jsx';
import QuoteEditorPage from './pages/QuoteEditorPage.jsx';
import SalesPage from './pages/SalesPage.jsx';
import ExpensesPage from './pages/ExpensesPage.jsx';
import CashPage from './pages/CashPage.jsx';
import CashClosePage from './pages/CashClosePage.jsx';
import BotQuestionsPage from './pages/BotQuestionsPage.jsx';
import OwnerReportPage from './pages/OwnerReportPage.jsx';
import FollowupsPage from './pages/FollowupsPage.jsx';
import ContentStudioPage from './pages/ContentStudioPage.jsx';
import CompetitorPage from './pages/CompetitorPage.jsx';
import AdsPage from './pages/AdsPage.jsx';
import SaleDetailPage from './pages/SaleDetailPage.jsx';
import NewSalePage from './pages/NewSalePage.jsx';
import PublicQuotePage from './pages/PublicQuotePage.jsx';

import KitchenMonitor from './components/KitchenMonitor.jsx';
import StaffPortal from './components/StaffPortal.jsx';
import DeliveryPortal from './components/DeliveryPortal.jsx';
import BookingPortal from './components/BookingPortal.jsx';
import SaaSLandingPage from './components/SaaSLandingPage.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import Academy from './components/Academy.jsx';
import PricingPage from './components/PricingPage.jsx';
import SolutionsPage from './components/SolutionsPage.jsx';

import FeatureGate from './components/FeatureGate.jsx';
import LegacyBookRedirect from './components/LegacyBookRedirect.jsx';

// Wrappers for components that expected props from window.location
const BookingWrapper = () => {
  const { slug } = useParams();
  return <BookingPortal slug={slug} />;
};

const SolutionsWrapper = () => {
  const { module } = useParams();
  return <SolutionsPage module={module || 'agenda'} />;
};

export const router = createBrowserRouter([
  {
    path: '/',
    element: <SaaSLandingPage />,
  },
  {
    path: '/cocina',
    element: <KitchenMonitor />,
  },
  {
    path: '/staff',
    element: <StaffPortal />,
  },
  {
    path: '/delivery',
    element: <DeliveryPortal />,
  },
  {
    path: '/app/:slug',
    element: <BookingWrapper />,
  },
  {
    path: '/book/:slug',
    element: <LegacyBookRedirect />,
  },
  {
    path: '/pedidos/:slug',
    element: <BookingWrapper />,
  },
  {
    path: '/cotizacion/:token',
    element: <PublicQuotePage />,
  },
  {
    path: '/academy',
    element: <Academy />,
  },
  {
    path: '/planes',
    element: <PricingPage />,
  },
  {
    path: '/soluciones',
    element: <SolutionsWrapper />,
  },
  {
    path: '/soluciones/:module',
    element: <SolutionsWrapper />,
  },
  // We point the backend/dashboard routes to App for now until it's fully decoupled
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/signup',
    element: <Login />,
  },
  {
    path: '/admin',
    element: <Login />,
  },
  {
    path: '/master/dashboard',
    element: <ProtectedRoute><App /></ProtectedRoute>,
  },
  {
    path: '/master/clients',
    element: <ProtectedRoute><App /></ProtectedRoute>,
  },
  {
    path: '/master/plans',
    element: <ProtectedRoute><App /></ProtectedRoute>,
  },
  {
    path: '/master/profile',
    element: <ProtectedRoute><App /></ProtectedRoute>,
  },
  {
    path: '/master/vault',
    element: <ProtectedRoute><App /></ProtectedRoute>,
  },
  {
    path: '/v1/dashboard',
    element: <Navigate to="/v2/dashboard" replace />,
  },
  {
    path: '/dashboard',
    element: <Navigate to="/v2/dashboard" replace />, // Quick redirect for legacy bookmarks
  },
  {
    path: '/v2',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: 'dashboard', element: <BusinessDashboard /> },
      { path: 'agenda', element: <FeatureGate moduleKey="feature_appointments"><AgendaPage /></FeatureGate> },
      { path: 'clientes', element: <CustomersPage /> },
      { path: 'crm', element: <FeatureGate moduleKey="feature_appointments"><KanbanPage /></FeatureGate> },
      { path: 'pedidos', element: <FeatureGate moduleKey="feature_orders"><OrdersPage /></FeatureGate> },
      { path: 'servicios', element: <FeatureGate moduleKey="feature_services"><ServicesPage /></FeatureGate> },
      { path: 'inventario', element: <FeatureGate moduleKey="feature_inventory"><InventoryPage /></FeatureGate> },
      { path: 'comisiones', element: <FeatureGate moduleKey="feature_commissions"><CommissionsPage /></FeatureGate> },
      { path: 'marketing', element: <FeatureGate moduleKey="feature_campaigns"><CampaignsPage /></FeatureGate> },
      { path: 'prospeccion', element: <FeatureGate moduleKey="feature_prospecting" hidden><ProspectingPage /></FeatureGate> },
      { path: 'catalogo', element: <FeatureGate moduleKey="feature_catalogo" hidden><CatalogPage /></FeatureGate> },
      { path: 'cotizaciones', element: <FeatureGate moduleKey="feature_cotizaciones" hidden><QuotesPage /></FeatureGate> },
      { path: 'cotizaciones/:id', element: <FeatureGate moduleKey="feature_cotizaciones" hidden><QuoteEditorPage /></FeatureGate> },
      { path: 'ventas', element: <FeatureGate moduleKey="feature_cotizaciones" hidden><SalesPage /></FeatureGate> },
      { path: 'ventas/nueva', element: <FeatureGate moduleKey="feature_cotizaciones" hidden><NewSalePage /></FeatureGate> },
      { path: 'ventas/:id', element: <FeatureGate moduleKey="feature_cotizaciones" hidden><SaleDetailPage /></FeatureGate> },
      { path: 'fidelizacion', element: <FeatureGate moduleKey="feature_loyalty"><LoyaltyPage /></FeatureGate> },
      { path: 'academy', element: <AcademyPage /> },
      { path: 'finances', element: <FeatureGate moduleKey="feature_finanzas"><FinancesPage /></FeatureGate> },
      { path: 'caja', element: <FeatureGate moduleKey="feature_finanzas"><CashPage /></FeatureGate> },
      { path: 'cierre', element: <FeatureGate moduleKey="feature_finanzas"><CashClosePage /></FeatureGate> },
      { path: 'gastos', element: <FeatureGate moduleKey="feature_finanzas"><ExpensesPage /></FeatureGate> },
      { path: 'staff', element: <FeatureGate moduleKey="feature_staff"><StaffPage /></FeatureGate> },
      { path: 'config', element: <ConfigPage /> },
      { path: 'preguntas-bot', element: <FeatureGate moduleKey="feature_bot_aprende"><BotQuestionsPage /></FeatureGate> },
      { path: 'reporte-semanal', element: <FeatureGate moduleKey="feature_reportes"><OwnerReportPage /></FeatureGate> },
      { path: 'seguimiento', element: <FeatureGate moduleKey="feature_seguimiento"><FollowupsPage /></FeatureGate> },
      { path: 'contenido', element: <FeatureGate moduleKey="feature_contenido"><ContentStudioPage /></FeatureGate> },
      { path: 'competencia', element: <FeatureGate moduleKey="feature_reportes"><CompetitorPage /></FeatureGate> },
      { path: 'anuncios', element: <FeatureGate moduleKey="feature_ads"><AdsPage /></FeatureGate> },
      { path: 'minisitio', element: <MinisitioPage /> },
      { path: 'cuenta', element: <AccountPage /> },
      { path: 'menu-media', element: <FeatureGate moduleKey="feature_menu_media"><MenuMediaPage /></FeatureGate> }
    ]
  },
  {
    path: '*',
    element: <SaaSLandingPage />, // Fallback
  }
]);
